import { useCallback, useEffect, useRef, useState } from 'react'
import { type RefObject } from 'react'
import { type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { motion } from 'framer-motion'
import * as Popover from '@radix-ui/react-popover'
import { type Edge } from '@xyflow/react'

import { F0Box, F0Button, F0Icon, F0Text } from '@factorialco/f0-react'
import { F1SearchBox } from '@factorialco/f0-react/dist/experimental'
import { Search } from '@factorialco/f0-react/icons/app'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { type CustomNode } from 'modules/job_catalog/components/JobCatalogTree/types'
import useSearchNodes, {
  type SearchNodeResult,
} from 'modules/job_catalog/components/JobCatalogTree/useSearchNodes'
import { nodeTypeIcon } from 'modules/job_catalog/lib/nodesUtils'

const SEARCH_WIDTH = 280
const SEARCH_DEBOUNCE_MS = 100
const SEARCH_RESULTS_LISTBOX_ID = 'job-catalog-tree-search-results'
const SEARCH_RESULTS_SCROLL_MAX_HEIGHT = 216
const SEARCH_RESULT_ROW_HEIGHT = '9'
const SEARCH_RESULTS_BOX_SHADOW = '0 4px 20px 0 hsl(var(--shadow) / 0.08)'
const SEARCH_RESULTS_Z_INDEX = 1100

const isNodeTarget = (value: EventTarget | null): value is Node => value instanceof Node

const containerAnimation = {
  initial: { width: 40, opacity: 0.96 },
  animate: { opacity: 1 },
  transition: { duration: 0.15, ease: 'easeInOut' },
} as const

interface Props {
  edges: Edge[]
  nodes: CustomNode[]
}

interface SearchResultRowProps {
  active?: boolean
  hovered?: boolean
  resultRef?: RefObject<HTMLElement>
  onHover?: () => void
  onHoverEnd?: () => void
  onSelect: (nodeId: string) => void
  result: SearchNodeResult
}

interface SearchResultsPopoverContentProps {
  activeIndex: number
  activeResultRef: RefObject<HTMLElement>
  showActiveOutline: boolean
  hoverIndex: number | null
  listboxId: string
  noResultsLabel: string
  onHover: (index: number) => void
  onHoverEnd: () => void
  onSelect: (nodeId: string) => void
  results: SearchNodeResult[]
}

const SearchResultRow = ({
  active = false,
  hovered = false,
  resultRef,
  onHover,
  onHoverEnd,
  onSelect,
  result,
}: SearchResultRowProps) => {
  const hasLevelSuffix = result.type === 'level' && result.ancestorName && result.levelName

  return (
    <F0Box
      ref={resultRef}
      id={`${SEARCH_RESULTS_LISTBOX_ID}-${result.id}`}
      role="option"
      aria-selected={active}
      data-active={active || undefined}
      tabIndex={-1}
      onMouseDown={(event) => {
        event.preventDefault()
        onSelect(result.id)
      }}
      onMouseEnter={onHover}
      onMouseLeave={onHoverEnd}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onSelect(result.id)
        }
      }}
      position="relative"
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="start"
      gap="md"
      width="full"
      paddingX="sm"
      paddingY="none"
      height={SEARCH_RESULT_ROW_HEIGHT}
      minHeight={SEARCH_RESULT_ROW_HEIGHT}
      maxHeight={SEARCH_RESULT_ROW_HEIGHT}
      borderRadius="md"
      background={hovered || active ? 'tertiary' : 'transparent'}
    >
      <F0Box display="flex" alignItems="center" justifyContent="center">
        <F0Icon icon={nodeTypeIcon(result.type)} size="md" color="default" />
      </F0Box>
      <F0Box display="flex" alignItems="center" minWidth={0} overflow="hidden" gap="xs" grow>
        {hasLevelSuffix && result.ancestorName && result.levelName ? (
          <F0Text as="span" content={result.ancestorName} variant="description" ellipsis />
        ) : null}
        {hasLevelSuffix && result.ancestorName && result.levelName ? (
          <F0Text as="span" content="/" variant="description" />
        ) : null}
        {hasLevelSuffix && result.ancestorName && result.levelName ? (
          <F0Text as="span" content={result.levelName} variant="body" ellipsis />
        ) : (
          <F0Text as="span" content={result.displayName} variant="body" ellipsis />
        )}
      </F0Box>
    </F0Box>
  )
}

const SearchResultsPopoverContent = ({
  activeIndex,
  activeResultRef,
  showActiveOutline,
  hoverIndex,
  listboxId,
  noResultsLabel,
  onHover,
  onHoverEnd,
  onSelect,
  results,
}: SearchResultsPopoverContentProps) => (
  <motion.div
    initial={{ opacity: 0, y: -4 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.15, ease: 'easeOut' }}
    style={{
      overflow: 'hidden',
    }}
  >
    {/* eslint-disable-next-line factorial/no-raw-html */}
    <div style={{ boxShadow: SEARCH_RESULTS_BOX_SHADOW, overflow: 'hidden', borderRadius: 16 }}>
      <F0Box
        overflow="hidden"
        border="default"
        borderColor="secondary"
        borderRadius="xl"
        background="primary"
      >
        {/* eslint-disable-next-line factorial/no-raw-html */}
        <div style={{ paddingTop: 4, paddingBottom: 4 }}>
          {/* eslint-disable-next-line factorial/no-raw-html */}
          <div style={{ maxHeight: SEARCH_RESULTS_SCROLL_MAX_HEIGHT, overflowY: 'auto' }}>
            <F0Box
              id={listboxId}
              role="listbox"
              display="flex"
              flexDirection="column"
              gap="none"
              paddingX="xs"
              paddingY="none"
            >
              {results.length > 0 ? (
                results.map((result, index) => (
                  <SearchResultRow
                    resultRef={index === activeIndex ? activeResultRef : undefined}
                    key={result.id}
                    result={result}
                    hovered={hoverIndex === index}
                    onHover={() => onHover(index)}
                    onHoverEnd={onHoverEnd}
                    onSelect={onSelect}
                    active={showActiveOutline && index === activeIndex}
                  />
                ))
              ) : (
                <F0Box paddingX="md" paddingY="sm">
                  <F0Text content={noResultsLabel} color="secondary" variant="body" />
                </F0Box>
              )}
            </F0Box>
          </div>
        </div>
      </F0Box>
    </div>
  </motion.div>
)

const SearchNodes = ({ edges, nodes }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [showActiveOutline, setShowActiveOutline] = useState(false)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const triggerButtonRef = useRef<HTMLButtonElement>(null)
  const activeResultRef = useRef<HTMLElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const popoverContentRef = useRef<HTMLDivElement>(null)
  const { results, hasSearch } = useSearchNodes(nodes, edges, search)
  const searchLabel = i18n.t('common.search')
  const noResultsLabel = i18n.t('job_catalog.job_catalog_tree.search.no_results')
  const showResults = isOpen && hasSearch

  const closeSearch = useCallback(() => {
    setSearch('')
    setIsOpen(false)
    setActiveIndex(0)
    setShowActiveOutline(false)
    setHoverIndex(null)
  }, [])

  const handleSelectNode = useCallback(
    (nodeId: string) => {
      const selected = results.find((r) => r.id === nodeId)
      if (selected) {
        track(trackingEvents.jobCatalog.searchNodeSubmit, {
          event_category: 'Organizations',
          search_query: search,
          node_type: selected.type,
          node_name: selected.name,
        })
      }
      navigate(navigation.employees.jobCatalog.overview.node.fullPath({ id: nodeId }))
      closeSearch()
    },
    [closeSearch, navigate, navigation.employees.jobCatalog.overview.node, results, search]
  )

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        isNodeTarget(event.target) &&
        !containerRef.current?.contains(event.target) &&
        !popoverContentRef.current?.contains(event.target)
      ) {
        closeSearch()
      }
    }

    const timeoutId = window.setTimeout(() => {
      document.addEventListener('click', handleClickOutside)
    }, 0)

    return () => {
      window.clearTimeout(timeoutId)
      document.removeEventListener('click', handleClickOutside)
    }
  }, [closeSearch, isOpen])

  useEffect(() => {
    setActiveIndex(0)
    setShowActiveOutline(false)
    setHoverIndex(null)
  }, [search])

  useEffect(() => {
    if (!showResults) {
      return
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      activeResultRef.current?.scrollIntoView({ block: 'nearest' })
    })

    return () => {
      window.cancelAnimationFrame(animationFrameId)
    }
  }, [activeIndex, results, showResults])

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!results.length) {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeSearch()
      }

      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!showActiveOutline) {
        setActiveIndex(0)
        setShowActiveOutline(true)
      } else {
        setActiveIndex((currentIndex) => (currentIndex + 1) % results.length)
      }
      setHoverIndex(null)
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (!showActiveOutline) {
        setActiveIndex(results.length - 1)
        setShowActiveOutline(true)
      } else {
        setActiveIndex((currentIndex) => (currentIndex - 1 + results.length) % results.length)
      }
      setHoverIndex(null)
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      const selectedResult = results[activeIndex]

      if (selectedResult) {
        handleSelectNode(selectedResult.id)
      }

      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      closeSearch()
    }
  }

  return (
    <F0Box display="flex" flexDirection="column" gap="sm">
      <motion.div
        {...containerAnimation}
        ref={containerRef}
        animate={{ width: isOpen ? SEARCH_WIDTH : 40, opacity: 1 }}
        style={{ overflow: 'hidden' }}
      >
        {isOpen ? (
          <Popover.Root open={showResults}>
            <Popover.Anchor asChild>
              <F0Box
                onKeyDown={handleSearchKeyDown}
                role="combobox"
                aria-expanded={showResults}
                aria-controls={SEARCH_RESULTS_LISTBOX_ID}
                aria-haspopup="listbox"
                aria-activedescendant={
                  showResults && results[activeIndex]
                    ? `${SEARCH_RESULTS_LISTBOX_ID}-${results[activeIndex].id}`
                    : undefined
                }
              >
                <F1SearchBox
                  autoFocus
                  debounceTime={SEARCH_DEBOUNCE_MS}
                  placeholder={searchLabel}
                  value={search}
                  onChange={(value) => {
                    setSearch(value)
                    setIsOpen(true)
                    setShowActiveOutline(false)
                  }}
                  onFocus={() => {
                    setIsOpen(true)
                    setShowActiveOutline(false)
                  }}
                  clearable
                />
              </F0Box>
            </Popover.Anchor>
            <Popover.Portal>
              <Popover.Content
                ref={popoverContentRef}
                align="start"
                side="bottom"
                sideOffset={4}
                onOpenAutoFocus={(event) => event.preventDefault()}
                onCloseAutoFocus={(event) => event.preventDefault()}
                onInteractOutside={() => closeSearch()}
                style={{
                  width: `${SEARCH_WIDTH}px`,
                  zIndex: SEARCH_RESULTS_Z_INDEX,
                  background: 'transparent',
                }}
              >
                <SearchResultsPopoverContent
                  activeIndex={activeIndex}
                  activeResultRef={activeResultRef}
                  showActiveOutline={showActiveOutline}
                  hoverIndex={hoverIndex}
                  listboxId={SEARCH_RESULTS_LISTBOX_ID}
                  noResultsLabel={noResultsLabel}
                  onHover={setHoverIndex}
                  onHoverEnd={() => setHoverIndex(null)}
                  onSelect={handleSelectNode}
                  results={results}
                />
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        ) : (
          <F0Button
            ref={triggerButtonRef}
            icon={Search}
            label={searchLabel}
            hideLabel
            round
            variant="outline"
            onClick={() => setIsOpen(true)}
          />
        )}
      </motion.div>
    </F0Box>
  )
}

export default SearchNodes
